import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, Dumbbell, Pencil, Trash2, ChevronRight, CloudOff } from "lucide-react";
import { getQueue, removeFromQueue, onQueueChange } from "@/lib/offlineQueue";
import { cacheGet, cacheSet } from "@/lib/cache";

export default function Entries() {
  const t = useT();
  const [entries, setEntries] = useState(null);
  const [type, setType] = useState("");
  const [pending, setPending] = useState(getQueue());

  const load = async () => {
    const qs = type ? `?entry_type=${type}` : "";
    const cacheKey = `entries${qs}`;
    try {
      const { data } = await api.get(`/entries${qs}`);
      setEntries(data);
      cacheSet(cacheKey, data);
    } catch {
      setEntries((prev) => prev || cacheGet(cacheKey) || []); // offline → cached/empty
    }
  };
  useEffect(() => { load(); }, [type]);

  // Keep the pending list in sync; when the queue drains, refresh server list.
  useEffect(() => {
    const off = onQueueChange((count) => {
      setPending(getQueue());
      if (count === 0) load();
    });
    return off;
  }, [type]); // eslint-disable-line

  const del = async (id) => { await api.delete(`/entries/${id}`); toast.success(t("Entry deleted")); load(); };
  const discardPending = (client_id) => { removeFromQueue(client_id); setPending(getQueue()); toast(t("Removed from queue")); };

  const visiblePending = pending.filter((p) => !type || p.payload?.entry_type === type);
  const nothing = entries && entries.length === 0 && visiblePending.length === 0;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-head font-bold text-3xl">{t("My Entries")}</h1>
        <Link to="/app/log"><Button className="bg-brand hover:bg-brand-dark" data-testid="new-entry-btn">+ {t("New")}</Button></Link>
      </div>
      <div className="flex gap-2 mb-4">
        {[["", "All"], ["tournament", "Tournament"], ["practice", "Practice"]].map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} data-testid={`entries-filter-${v || "all"}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border-2 ${type === v ? "border-brand bg-brand text-white" : "border-slate-200 bg-white"}`}>{t(l)}</button>
        ))}
      </div>

      {/* Pending offline entries */}
      {visiblePending.length > 0 && (
        <div className="space-y-3 mb-3" data-testid="pending-entries-list">
          {visiblePending.map((p) => {
            const e = p.payload || {};
            const isTour = e.entry_type === "tournament";
            return (
              <div key={p.client_id} className="ct-card p-4 flex items-center gap-4 border-2 border-amber-300 bg-amber-50/60" data-testid={`pending-entry-${p.client_id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: isTour ? "#D90416" : "#1E293B" }}>
                  {isTour ? <Trophy size={18} /> : <Dumbbell size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.opponent || (isTour ? t("Match") : t("Practice"))} <span className="text-slate-400 text-sm font-normal">· {e.date}</span></div>
                  <div className="text-sm flex gap-2 items-center flex-wrap">
                    {e.batting && <span className="text-red-600 font-mono font-bold">{e.batting.runs}{e.batting.not_out ? "*" : ""} ({e.batting.balls})</span>}
                    {e.bowling && <span className="text-blue-800 font-mono font-bold">{e.bowling.wickets}/{e.bowling.runs_conceded} ({e.bowling.overs})</span>}
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-200/70 rounded-full px-2 py-0.5" data-testid="waiting-to-sync-tag">
                      <CloudOff size={11} /> {t("Waiting to sync")}
                    </span>
                  </div>
                </div>
                <button onClick={() => discardPending(p.client_id)} className="p-2 text-amber-600 hover:text-brand shrink-0" data-testid={`discard-pending-${p.client_id}`} title={t("Discard")}><Trash2 size={16} /></button>
              </div>
            );
          })}
        </div>
      )}

      {!entries ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-20" />)}</div>
      ) : nothing ? (
        <div className="ct-card p-10 text-center" data-testid="entries-empty">
          <p className="text-slate-500 mb-4">{t("No entries yet.")}</p>
          <Link to="/app/log"><Button className="bg-brand hover:bg-brand-dark">{t("Log your first performance")}</Button></Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="entries-list">
          {entries.map((e) => {
            const isTour = e.entry_type === "tournament";
            return (
              <div key={e.id} className="ct-card p-4 flex items-center gap-4" data-testid={`entry-row-${e.id}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0`} style={{ background: isTour ? "#D90416" : "#1E293B" }}>
                  {isTour ? <Trophy size={18} /> : <Dumbbell size={18} />}
                </div>
                <Link to={`/app/entries/${e.id}`} className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.opponent || (isTour ? t("Match") : t("Practice"))} <span className="text-slate-400 text-sm font-normal">· {e.date}</span></div>
                  <div className="text-sm text-slate-500 flex gap-3 flex-wrap">
                    {e.batting && <span className="text-red-600 font-mono font-bold">{e.batting.runs}{e.batting.not_out ? "*" : ""} ({e.batting.balls})</span>}
                    {e.bowling && <span className="text-blue-800 font-mono font-bold">{e.bowling.wickets}/{e.bowling.runs_conceded} ({e.bowling.overs})</span>}
                    <span className="text-slate-400">{e.tournament || e.practice_type}</span>
                  </div>
                </Link>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/app/log/${e.id}`} className="p-2 text-slate-400 hover:text-slate-900" data-testid={`edit-${e.id}`}><Pencil size={16} /></Link>
                  <button onClick={() => del(e.id)} className="p-2 text-slate-400 hover:text-brand" data-testid={`delete-${e.id}`}><Trash2 size={16} /></button>
                  <Link to={`/app/entries/${e.id}`}><ChevronRight className="text-slate-300" size={18} /></Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
